/**
 * ledgerStatementOriginalTotalWords
 *
 * The words of a statement import checked against its original's printed total: the review's line for an
 * `LedgerStatementOriginalTotal` ("Acme $624.00 of $1,794.00 printed on the original statement. $1,170.00 is
 * still to import." short of it, "Acme $624.00 + Globex $1,170.00 = $1,794.00, as printed on the original
 * statement." on it, "Acme $294.35 against $294.34 printed on the original statement. The $0.01 difference comes
 * from rounding." within the rounding tolerance the check allows either way), and the
 * message of each `LedgerStatementOriginalRefusal`. Every one says "printed on the original statement". Consumers:
 * `LedgerStatementProposalReview` and `ledgerStatementImportRefusal` in react, which name each counterparty by its
 * record's label, and `ledgerStatementRefusalMessage` in functions, which has no labels and names it by its id.
 * Amounts read through `ledgerMoney`. Imports are listed in the order given, this file last.
 */

import { ledgerMoney } from './ledgerMoney';
import type { LedgerStatementOriginalRefusal, LedgerStatementOriginalTotal } from '../System.Ledger.types';

export const ledgerStatementOriginalTotalWords = (
    subject: LedgerStatementOriginalTotal | LedgerStatementOriginalRefusal,
    currency: string,
    counterpartyName: (counterpartyId: string) => string,
): string => {
    const money = (minor: number): string => ledgerMoney(minor, currency);
    if ('kind' in subject && subject.kind === 'originalTotalMismatch') {
        return `This file gives ${money(subject.printedTotalMinor)} as the total printed on the original statement, but the`
            + ` ${counterpartyName(subject.standing.counterpartyId)} import from the same original gives`
            + ` ${money(subject.standing.printedTotalMinor)}. Nothing was imported. Correct the original statement total in`
            + ' this file, or reverse that payment if its total is the wrong one, then import this file again.';
    }
    const { printedTotalMinor, imports } = subject;
    const importedMinor = imports.reduce((sum, entry) => sum + entry.totalMinor, 0);
    const listed = imports.map((entry) => `${counterpartyName(entry.counterpartyId)} ${money(entry.totalMinor)}`).join(' + ');
    const summed = imports.length === 1 ? listed : `${listed} = ${money(importedMinor)}`;
    if ('kind' in subject) {
        return `${summed}, ${money(importedMinor - printedTotalMinor)} more than the ${money(printedTotalMinor)} printed on`
            + ' the original statement. Nothing was imported. Check this file\'s lines and total against the original'
            + ' statement; if an earlier import from it is wrong, reverse that payment. Then import this file again.';
    }
    if (importedMinor === printedTotalMinor) return `${listed} = ${money(printedTotalMinor)}, as printed on the original statement.`;
    return 2 * Math.abs(printedTotalMinor - importedMinor) <= subject.rounded
        ? `${summed} against ${money(printedTotalMinor)} printed on the original statement.`
            + ` The ${money(Math.abs(printedTotalMinor - importedMinor))} difference comes from rounding.`
        : `${listed} of ${money(printedTotalMinor)} printed on the original statement.`
            + ` ${money(printedTotalMinor - importedMinor)} is still to import.`;
};
